<script setup lang="ts">
/**
 * Die Gestaltungsfläche: rendert das Dokument mit exakt den Styles des Druck-Renderers
 * (styleForLayer/baseCss aus @pfe/design-renderer). DOM-Konvention siehe baseCss.ts.
 * Overlays (Guides, Handles) liegen als Geschwister darüber, nie in diesem Baum.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import {
  BASE_CSS, imageClipStyle, imageElementStyle, layerStyle, productClipPath, rootStyle, shapeSvgInner, textStyle,
  type DesignDocument, type ImageLayer, type Layer,
} from '@pfe/design-renderer'

const props = withDefaults(defineProps<{
  doc: DesignDocument
  pxPerMm: number
  productView?: boolean
  showPlaceholders?: boolean
  editingTextId?: string | null
}>(), { productView: false, showPlaceholders: true, editingTextId: null })

const emit = defineEmits<{ (e: 'text-input', id: string, text: string): void; (e: 'text-done'): void }>()

/**
 * Direkt auf der Fläche schreiben: Während der Bearbeitung zeigt das Feld einen festen Startwert und
 * wird nicht von Vue neu gezeichnet (sonst springt der Cursor bei jedem Tastendruck). Der Schlüssel
 * wechselt beim Start/Ende, damit Vue das Element sauber neu aufbaut.
 */
const editStartText = ref('')
const rootEl = ref<HTMLElement | null>(null)
watch(() => props.editingTextId, async (id) => {
  if (!id) return
  const layer = props.doc.layers.find((l) => l.id === id)
  editStartText.value = layer && layer.type === 'text' ? layer.text : ''
  await nextTick()
  const el = rootEl.value?.querySelector<HTMLElement>(`[data-layer-id="${id}"] .dr-text`)
  if (!el) return
  el.focus({ preventScroll: true })
  // gesamten Text markieren: sofort lostippen ersetzt den Platzhaltertext
  const range = document.createRange()
  range.selectNodeContents(el)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}, { flush: 'post' })
function onTextKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') { e.preventDefault(); (e.target as HTMLElement).blur(); emit('text-done') }
}

const STYLE_ID = 'pfe-design-renderer-base-css'
onMounted(() => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.textContent = BASE_CSS
  document.head.appendChild(el)
})

const root = computed(() => {
  const style = rootStyle(props.doc.canvas, props.pxPerMm)
  if (props.productView) style.clipPath = productClipPath(props.doc.canvas)
  return style
})

function sourceOf(layer: ImageLayer): { url: string; width: number; height: number } | null {
  const s = layer.source
  if (s.kind === 'placeholder' || !s.url) return null
  return { url: s.url, width: s.width || 1000, height: s.height || 1000 }
}
function imgStyle(layer: ImageLayer) {
  const src = sourceOf(layer)
  return src ? imageElementStyle(layer, src) : {}
}
function maskDef(layer: ImageLayer): string | null {
  return (layer.mask.kind === 'svg' || layer.mask.kind === 'asset') && layer.mask.path ? `dr-mask-${layer.id}` : null
}
function bg() {
  const b = props.doc.canvas.background
  if (b.type === 'color') return { background: b.value }
  if (b.type === 'gradient') return { background: `linear-gradient(${b.angleDeg}deg,${b.stops.map((s) => `${s.color} ${Math.round(s.offset * 100)}%`).join(',')})` }
  return { background: '#FFFFFF' }
}
function bgImage(): { url: string; style: Record<string, string> } | null {
  const b = props.doc.canvas.background
  if ((b.type !== 'upload' && b.type !== 'asset') || !b.url) return null
  const c = props.doc.canvas
  const pseudo: ImageLayer = {
    id: 'bg', type: 'image', visible: true, locked: true, opacity: 1,
    frame: { xMm: -c.bleedMm, yMm: -c.bleedMm, wMm: c.widthMm + 2 * c.bleedMm, hMm: c.heightMm + 2 * c.bleedMm, rotationDeg: 0 },
    constraints: { locked: true, contentOnly: false, optional: false },
    source: { kind: 'placeholder' }, fit: (b.type === 'upload' && b.fit) ? b.fit : { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 },
    mask: { kind: 'none' }, filters: { grayscale: false, sepia: 0, brightness: 0, contrast: 0, saturation: 0 }, border: null, shadow: null,
  }
  const dims = { width: (b as any).width || 1000, height: (b as any).height || 1000 }
  return { url: b.url, style: imageElementStyle(pseudo, dims) }
}
function shape(layer: Extract<Layer, { type: 'shape' }>) {
  return shapeSvgInner(layer)
}
function onTextInput(id: string, e: Event) {
  emit('text-input', id, String((e.target as HTMLElement).innerText ?? ''))
}
</script>

<template>
  <div ref="rootEl" class="dr-root dr-root--edit" :style="root" :data-canvas-w="doc.canvas.widthMm + 2 * doc.canvas.bleedMm" :data-canvas-h="doc.canvas.heightMm + 2 * doc.canvas.bleedMm">
    <svg v-if="doc.layers.some((l) => l.type === 'image' && maskDef(l as ImageLayer))" class="dr-mask-defs" aria-hidden="true">
      <defs>
        <template v-for="l in doc.layers" :key="'m' + l.id">
          <clipPath v-if="l.type === 'image' && maskDef(l)" :id="maskDef(l)!" clipPathUnits="objectBoundingBox">
            <path :d="(l.mask as any).path" />
          </clipPath>
        </template>
      </defs>
    </svg>
    <div class="dr-bg" :style="bg()">
      <img v-if="bgImage()" class="dr-bg-img" :src="bgImage()!.url" alt="" draggable="false" :style="bgImage()!.style" />
    </div>
    <template v-for="layer in doc.layers" :key="layer.id">
      <div
        class="dr-layer"
        :class="[`dr-layer--${layer.type}`, { 'dr-layer--hidden': !layer.visible }]"
        :data-layer-id="layer.id"
        :style="layerStyle(layer, doc.canvas)"
      >
        <template v-if="layer.type === 'image'">
          <div class="dr-image-clip" :style="imageClipStyle(layer, maskDef(layer) || undefined)">
            <img v-if="sourceOf(layer)" class="dr-image" :src="sourceOf(layer)!.url" alt="" draggable="false" :style="imgStyle(layer)" />
            <div v-else-if="showPlaceholders" class="dr-placeholder" :data-slot-index="layer.source.kind === 'placeholder' ? (layer.source.slotIndex ?? '') : ''" />
          </div>
        </template>
        <template v-else-if="layer.type === 'text'">
          <div
            v-if="editingTextId === layer.id"
            :key="`${layer.id}:edit`"
            class="dr-text dr-text--editing"
            :style="textStyle(layer)"
            contenteditable="plaintext-only"
            spellcheck="true"
            role="textbox"
            aria-multiline="true"
            aria-label="Text bearbeiten"
            @input="onTextInput(layer.id, $event)"
            @keydown="onTextKeydown"
          >{{ editStartText }}</div>
          <div v-else :key="`${layer.id}:view`" class="dr-text" :style="textStyle(layer)"><span>{{ layer.text }}</span></div>
        </template>
        <template v-else-if="layer.type === 'sticker'">
          <img v-if="layer.source.url" class="dr-sticker" :src="layer.source.url" alt="" draggable="false" :style="(layer.flipH || layer.flipV) ? { transform: `${layer.flipH ? 'scaleX(-1)' : ''} ${layer.flipV ? 'scaleY(-1)' : ''}` } : undefined" />
        </template>
        <template v-else>
          <svg class="dr-shape" :viewBox="shape(layer).viewBox" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" v-html="shape(layer).body" />
        </template>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Die Bühne sperrt Textauswahl (Ziehen/Skalieren); im bearbeiteten Text muss sie wieder gehen */
.dr-text--editing {
  -webkit-user-select: text;
  user-select: text;
  cursor: text;
  outline: 2px solid var(--pfe-color-accent, #e30b17);
  outline-offset: 2px;
  caret-color: currentColor;
  pointer-events: auto;
}
</style>
